<template>
  <section class="user-dashboard__menu">
    <div class="user-dashboard__menu-options">
      <div
        v-for="(option, i) in options"
        :key="`menu-option-${i}`"
        @click="updateSelection(option)"
        :class="
          option === currentOption
            ? 'user-dashboard__menu-option selected'
            : 'user-dashboard__menu-option'
        "
      >
        {{ option }}
      </div>
    </div>
    <div v-if="!isComponentLoading" class="user-dashboard__menu-components">
      <div v-if="currentOption === 'Profile'">
        <UserDashboardProfile />
      </div>
      <div v-if="currentOption === 'Active Courses'">
        <UserDashboardActiveCourses
          @removeActiveCourse="removeActiveCourse"
          :courses="activeCourses"
        ></UserDashboardActiveCourses>
      </div>
      <div v-if="currentOption === 'Bookmarks'">
        <user-dashboard-bookmarks
          :bookmarks="bookmarks"
          @deleteBookmarkById="deleteBookmarkById"
        ></user-dashboard-bookmarks>
      </div>
      <div v-if="currentOption === 'Subscription'">
        <UserDashboardSubscription :activeSubscriptions="activeSubscriptions" />
      </div>
    </div>
    <div v-else class="user-dashboard__loader-wrapper">
      <BarLoader
        class="user-dashboard__loader"
        color="#ef7d22"
        :width="100"
        :height="6"
      />
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { BarLoader } from '@saeris/vue-spinners'
import UserDashboardProfile from './UserDashboardProfile.vue'
import UserDashboardActiveCourses from './UserDashboardActiveCourses.vue'
import UserDashboardBookmarks from './UserDashboardBookmarks.vue'
import UserDashboardSubscription from './UserDashboardSubscription.vue'

export default {
  name: 'UserDashboardMenu',
  data() {
    return {
      options: ['Profile', 'Active Courses', 'Bookmarks', 'Subscription'],
      currentOption: 'Active Courses',
    }
  },
  props: {
    routerOption: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapGetters({
      isComponentLoading: 'app/isComponentLoading',
      deleteActiveCourse: 'user/deleteActiveCourse',
      activeCourses: 'user/userActiveCourses',
      bookmarks: 'user/bookmarksByCourse',
      deleteBookmark: 'user/deleteBookmark',
      activeSubscriptions: 'user/activeSubscriptions',
      userId: 'auth/userId',
    }),
  },
  methods: {
    async updateSelection(option) {
      this.$store.dispatch('app/setIsComponentLoading', true)
      switch (option) {
        case 'Active Courses':
          await this.$store.dispatch('user/getUserActiveCourses', this.userId)
          break
        case 'Bookmarks':
          await this.$store.dispatch('user/getBookmarksByCourse', this.userId)
          break
        case 'Subscription':
          await this.$store.dispatch(
            'user/getUserActiveSubscriptions',
            this.memberId
          )
          break
        default:
          break
      }
      this.currentOption = option
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
    async removeActiveCourse(course) {
      const courseAndUserIds = { courseId: course.id, userId: this.userId }
      // eslint-disable-next-line
      if (confirm('Are you sure you want to remove this active course?')) {
        await this.$store.dispatch(
          'user/deleteFromUserActiveCourse',
          courseAndUserIds
        )
        await this.$store.dispatch('user/getUserActiveCourses', this.userId)
      }
    },
    updateAvatar(avatar) {
      this.$emit('updateAvatar', avatar)
    },
    async deleteBookmarkById(id) {
      // eslint-disable-next-line
      if (confirm('Are you sure you want to delete this bookmark?')) {
        await this.$store.dispatch('user/deleteBookmarkById', id)
        await this.$store.dispatch('user/getBookmarksByCourse', this.userId)
      }
    },
  },
  components: {
    UserDashboardProfile,
    UserDashboardActiveCourses,
    UserDashboardBookmarks,
    UserDashboardSubscription,
    BarLoader,
  },
  async created() {
    this.$store.dispatch('app/setIsComponentLoading', true)
    if (this.routerOption && this.routerOption !== '') {
      switch (this.routerOption) {
        case 'active-courses':
          await this.$store.dispatch('user/getUserActiveCourses', this.userId)
          this.currentOption = 'Active Courses'
          break
        case 'bookmarks':
          await this.$store.dispatch('user/getBookmarksByCourse', this.userId)
          this.currentOption = 'Bookmarks'
          break
        case 'profile':
          await this.$store.dispatch('user/getSampleAvatars')
          this.currentOption = 'Profile'
          break
        case 'subscription':
          await this.$store.dispatch(
            'user/getUserActiveSubscriptions',
            this.memberId
          )
          this.currentOption = 'Subscription'
          break
        default:
          await this.$store.dispatch('user/getSampleAvatars')
          this.currentOption = 'Profile'
          break
      }
    }
    this.$store.dispatch('app/setIsComponentLoading', false)
  },
}
</script>

<style lang="scss" scoped>
.user-dashboard {
  &__menu {
    min-height: 100vh;
    @include mobile {
      flex-grow: 1;
    }
  }
  &__menu-options {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    justify-content: space-evenly;
    flex-wrap: wrap;
    @include mobile {
      flex-wrap: nowrap;
    }
  }
  &__menu-option {
    color: var(--white);
    font-size: 1rem;
    white-space: nowrap;
    padding: 12px 0;
    cursor: pointer;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    @include mobile {
      margin: 0 50px 0 0;
      padding: 0 0 20px 0;
      font-size: 1.5rem;
      white-space: initial;
      border-bottom: none;
      &:last-child {
        margin: 0;
      }
    }
    &:hover {
      color: var(--orange);
    }
    &.selected {
      border-bottom: 2px solid var(--orange);
    }
  }
  &__loader-wrapper {
    width: 100%;
    margin: 150px auto 0 auto;
    text-align: center;
  }
  &__loader {
    margin: 0 auto;
  }
}
</style>
